import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { logActivity } from "@/lib/activity-logger";

type Profile = Tables<"profiles">;
type Workspace = Tables<"couple_workspaces">;

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  workspace: Workspace | null;
  partnerProfile: Profile | null;
  loading: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  workspace: null,
  partnerProfile: null,
  loading: true,
  isAdmin: false,
  signOut: async () => {},
  refreshProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [partnerProfile, setPartnerProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .single();
    setProfile(data);

    // Check admin role from user_roles table
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    setIsAdmin(roles?.some(r => r.role === "admin") ?? false);

    if (data?.workspace_id) {
      const { data: ws } = await supabase
        .from("couple_workspaces")
        .select("*")
        .eq("id", data.workspace_id)
        .single();
      setWorkspace(ws);

      // Fetch partner
      const { data: partners } = await supabase
        .from("profiles")
        .select("*")
        .eq("workspace_id", data.workspace_id)
        .neq("user_id", userId);
      if (partners && partners.length > 0) {
        setPartnerProfile(partners[0]);
      }
    }
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          if (_event === "SIGNED_IN") {
            logActivity("session_start");
            try { localStorage.setItem("together_os_email", session.user.email || ""); } catch {}
            if (window.location.hash.includes("access_token")) {
              window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
            }
          }
          setTimeout(() => fetchProfile(session.user.id), 0);
        } else {
          setProfile(null);
          setWorkspace(null);
          setPartnerProfile(null);
          setIsAdmin(false);
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        try { localStorage.setItem("together_os_email", session.user.email || ""); } catch {}
      }
      setLoading(false);
    });

    const handleFocus = () => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          setSession(session);
          setUser(session.user);
        }
      });
    };
    window.addEventListener("focus", handleFocus);

    const handleUnload = () => {
      logActivity("session_end");
    };
    window.addEventListener("beforeunload", handleUnload);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setWorkspace(null);
    setPartnerProfile(null);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ session, user, profile, workspace, partnerProfile, loading, isAdmin, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}
